/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import {
    ICartlinesViewProps,
    ICartResources,
    ICartViewProps,
    IOrderSummaryErrors
} from '@msdyn365-commerce-modules/cart';
import { IOrderSummaryLines } from '@msdyn365-commerce-modules/order-summary-utilities';
import { Button, INodeProps, Node } from '@msdyn365-commerce-modules/utilities';
import {
    IAddToOrderTemplateDialogResources, IDuplicateItemsWhenAddingToOrderTemplateDialogResources, IItemAddedToOrderTemplateDialogResources,
    IOrderTemplateNameDialogResources, IWishlistActionSuccessResult, PriceComponent
} from '@msdyn365-commerce/components';
import { IImageSettings } from '@msdyn365-commerce/core';
import { ICartActionResult, ICartState } from '@msdyn365-commerce/global-state';
import { AsyncResult, CartLine, CartLineValidationResults, LineDataValidationFailure } from '@msdyn365-commerce/retail-proxy';
import { validateForCheckoutAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import * as React from 'react';
import { CartLineItems } from '../../../components/citta-cart/cart-line-items';
import { ICartProps } from '../definition-extensions/cart.ext.props.autogenerated';

import CittaTelemetry from '../../../common/telemetry/citta-telemetry';
// import PriceComponent from '../../../components/price-component'

interface ICartViewState {
    cartlinesErrors: LineDataValidationFailure[];
}

/**
 *
 * CartView
 * @extends {React.PureComponent<ICartViewProps>}
 */
export class CartView extends React.PureComponent<ICartViewProps & ICartProps<{}>, ICartViewState>  {
    private cittaTelemetry: CittaTelemetry = new CittaTelemetry(this.props.context.app.config.cittaInstrumentationKey);

    public state: ICartViewState = {
        cartlinesErrors: []
    };

    private createLogMessage = (message: string): string => {
        const url: string = decodeURI(window.location.search);
        const origin = window.location.origin;
        const logMessage = `${message} Url: - ${url} - Origin: ${origin}`;
        return logMessage;
    }

    private logMessage = (message: string): void => {
        this.cittaTelemetry.trace(this.createLogMessage(message));
    }

    // private isQuantityLimitsFeatureEnabled: boolean = true;
    private isQuantityLimitsFeatureEnabled: boolean = false;


    constructor(props: ICartViewProps & ICartProps<{}>) {
        super(props);

        this._renderCartlines = this._renderCartlines.bind(this);
    }

    public async componentDidMount(): Promise<void> {
        // if (this.isQuantityLimitsFeatureEnabled) {
        //     await this._validateCart();
        // }
        this.isQuantityLimitsFeatureEnabled = await this.getOrderQuantityLimitsFeatureFlag();
        if (this.isQuantityLimitsFeatureEnabled) {
            await this._validateCart();
        }

        this.logMessage('landed on cart page');
    }

    public render(): JSX.Element | null {
        return (
            <div className={this.props.className} id={this.props.id} {...this.props.renderModuleAttributes(this.props)}>
                {this.props.title}
                <Node {...this.props.CartlinesWrapper}>
                    {this._renderCartlines()}
                </Node>
                {this.props.orderSummaryHeading &&
                    < Node {...this.props.OrderSummaryWrapper}>
                        {this.props.orderSummaryHeading}
                        {_renderOrderSummarylines(this.props.orderSummaryLineitems, this.props.OrderSummaryItems, this.props)}
                        {this.state.cartlinesErrors.length > 0 ? _renderErrorBlock(this.props.OrderSummaryErrors) : null}
                        {_renderUnder200sError(this.props)}
                        {this.props.config.freightCalculationText ? <div className='cart-freight-asterix'>{this.props.config.freightCalculationText}</div> : null}
                        <div className={'cart-checkout-button'}>
                            {this.props.checkoutAsSignInUserButton}
                            {this.props.context.request && this.props.context.request.user && this.props.context.request.user.isAuthenticated ? null : <div className='cart-checkout-button-option'>{this.props.resources.orLabel}</div>}
                            <div className={'cart-checkout-button-back-guest'}>
                                {_renderBackToShoppingButton(this.props.resources)}
                                {this.props.checkoutAsGuestButton}
                            </div>
                        </div>
                    </Node>
                }
                {this.props.storeSelector}
            </div>
        );
    }

    private _renderCartlines(): JSX.Element[] | JSX.Element {
        const { emptyCartText } = this.props.resources;

        if (this.props.cartLoadingStatus) {
            return <>{this.props.cartLoadingStatus}</>;
        }
        const cartLines = this._getCartLines();
        if (cartLines) {
            return cartLines.map((cartLine, index) => {
                return (
                    <div className='msc-cart-lines-item' key={index}>
                        {cartLine.cartline}
                        {_renderBOPISBlock(cartLine, this.props.resources, this.props.storeSelector)}
                        <div className='msc-cart-lines-item-buttons'>
                            {cartLine.addToWishlist}
                            {cartLine.remove}
                        </div>
                    </div>
                );
            });
        } else {
            return (this.props.cartDataResult ?
                (
                    <div className='msc-cart__empty-cart'>
                        <p className='msc-cart-line'>{emptyCartText}</p>
                        {this.props.backToShoppingButton}
                    </div>
                ) : <>{this.props.waitingComponent}</>
            );
        }
    }

    private _getCartLines(): ICartlinesViewProps[] | null {
        const {
            data: {
                cart: { result: cart },
                wishlists: { result: wishlists },
                orderTemplates: { result: orderTemplates },
                customerInformation: { result: customerInformation }
            },
            context: {
                request: {
                    channel: {
                        PickupDeliveryModeCode
                    } = { PickupDeliveryModeCode: undefined }
                }
            },
            config: config,
            telemetry,
            resources,
            modules
        } = this.props;

        const imageSettings = this._getImageSettings(config.imageSettings);

        const cartlinesErrors: LineDataValidationFailure[] = this.state.cartlinesErrors;
        let storeSelectorId: string = '';
        if (modules && Object.keys(modules).length > 0 && modules.storeSelector && modules.storeSelector.length > 0) {
            storeSelectorId = modules.storeSelector[0].id;
        }
        return this.props.data.products && this.props.data.products.result && cart && cart.cart.CartLines && cart.cart.CartLines.length > 0 ? (
            CartLineItems({
                cartlines: cart.cart.CartLines,
                storeSelectorModuleId: storeSelectorId,
                cartlinesErrors: cartlinesErrors,
                products: this.props.data.products.result,
                productAvailabilites: this.props.data.productAvailabilites.result,
                productDeliveryOptions: this.props.data.deliveryOptions.result,
                imageSettings: imageSettings,
                isStockCheckEnabled: this.props.context.app.config.enableStockCheck || false,
                maxCartlineQuantity: this.props.context.app.config.maxQuantityForCartLineItem,
                gridSettings: this.props.context.request.gridSettings!,
                context: this.props.context,
                typeName: this.props.typeName,
                id: this.props.id,
                resources: {
                    sizeString: "Size:" || resources.productDimensionTypeSize,
                    colorString: "Colour:" || resources.productDimensionTypeColor,
                    configString: resources.productDimensionTypeConfiguration,
                    styleString: resources.productDimensionTypeStyle,
                    quantityDisplayString: resources.quantityDisplayText,
                    inputQuantityAriaLabel: resources.inputQuantityAriaLabel,
                    decrementButtonAriaLabel: resources.decrementButtonAriaLabel,
                    incrementButtonAriaLabel: resources.incrementButtonAriaLabel,
                    discountStringText: resources.discountStringText,
                    originalPriceText: resources.originalPriceText,
                    currentPriceText: resources.currentPriceText,
                    shippingChargesText: resources.shippingCharges
                },
                telemetry: telemetry,
                addToWishlistButtonText: resources.addToWishlistButtonText,
                removeFromWishlistButtonText: resources.removeFromWishlistButtonText,
                removeButtonText: resources.removeCartButtonText,
                shipItText: resources.shipInsteadDisplayText,
                pickitUpText: resources.pickItUpDisplayText,
                changeStoreText: resources.changeStoreDisplayText,
                storeSelectorStateManager: this.props.data.storeSelectorStateManager.result,
                outOfStockText: resources.outOfStockErrorMessage,
                outOfRangeOneText: resources.errorMessageOutOfRangeOneLeft,
                outOfRangeFormatText: resources.errorMessageOutOfRangeFormat,
                cartState: this.props.data.cart.result,
                orgUnitLocations: this.props.data.orgUnitLocations.result,
                removeItemClickHandler: this._removeItemFromCartHandler,
                moveToWishlistSuccessHandler: this._moveToWishlistSuccessHandler,
                updateCartLinesQuantitySuccessHandler: this._updateCartlineQuantity,
                locationChangedHandler: this._fulfillmentStoreIdUpdated,
                wishlists: wishlists,
                defaultWishlistName: resources.defaultWishlistName,
                pickupDeliveryModeCode: PickupDeliveryModeCode,
                includeErrors: config.showOutOfStockErrors,
                showShippingChargesForLineItems: config.showShippingChargesForLineItems,
                telemetryContent: this.props.telemetryContent,
                isQuantityLimitsFeatureEnabled: this.isQuantityLimitsFeatureEnabled,

                addToOrderTemplateDialogResources: this._generateAddToOrderTemplateDialogResources(resources),
                createOrderTemplateDialogResources: this._generateOrderTemplateNameDialogResources(resources),
                itemAddedToOrderTemplateDialogResources: this._generateItemAddedToOrderTemplateDialogResources(resources),
                duplicateItemsWhenAddingToOrderTemplateDialogResources: this._generateDuplicateItemsWhenAddingToOrderTemplateDialogResources(resources),

                addToOrderTemplateButtonText: resources.addToOrderTemplateButtonText,
                addToOrderTemplateMessage: resources.addToOrderTemplateMessage,
                addItemToOrderTemplateError: resources.addItemToOrderTemplateError,
                orderTemplates: orderTemplates,
                customerInformation: customerInformation
            })
        ) : null;
    }

    private _getImageSettings = (imageSettings: IImageSettings | undefined): IImageSettings => {
        return imageSettings || {
            viewports: {
                xs: { q: `w=80&h=94&m=6`, w: 0, h: 0 },
                sm: { q: `w=148&h=174&m=6`, w: 0, h: 0 },
                lg: { q: `w=148&h=174&m=6`, w: 0, h: 0 }
            },
            lazyload: true
        };
    }

    private _removeItemFromCartHandler = (cartlineToRemove: CartLine) => {
        if (this.props.data.cart.result) {
            const input = {
                cartLineIds: [cartlineToRemove.LineId!.toString()]
            };
            this.props.data.cart.result.removeCartLines(input)
                .then(() => {
                    this._validateCart()
                        .catch((error) => {
                            this.props.telemetry.warning(error);
                            this.props.telemetry.debug('Unable to validate Cart');
                        });
                })
                .catch((error) => {
                    this.props.telemetry.warning(error);
                    this.props.telemetry.debug('Unable to Remove Cart Line');
                });
        }
    }

    private _moveToWishlistSuccessHandler = (result: IWishlistActionSuccessResult, cartline?: CartLine) => {
        if (result.status === 'ADDED' && cartline) {
            this._removeItemFromCartHandler(cartline);
        }
    }

    private _updateCartlineQuantity = (cartlineToUpdate: CartLine, quantity: number): boolean => {
        if (this.props.data.cart.result) {
            const input = {
                cartLineId: cartlineToUpdate.LineId!.toString(),
                newQuantity: quantity,
                additionalProperties: { orderQuantityLimitsFeatureIsEnabled: this.isQuantityLimitsFeatureEnabled }
            };
            this.props.data.cart.result.updateCartLineQuantity(input)
                .then((result: ICartActionResult) => {
                    if (result.status === 'SUCCESS') {
                        this._validateCart().catch((error) => {
                            this.props.telemetry.warning(error);
                            this.props.telemetry.debug('Unable to validate Cart');
                        });
                        return true;
                    }
                    return false;
                })
                .catch((error) => {
                    this.props.telemetry.warning(error);
                    this.props.telemetry.debug('Unable to update Cart Line quantity');
                });
        }

        return false;
    }

    private _fulfillmentStoreIdUpdated = () => {
        this._validateCart().catch((error) => {
            this.props.telemetry.warning(error);
            this.props.telemetry.debug('Unable to validate Cart');
        });
    }

    /**
     * Get order quantity limit feature flag.
     * @returns True if flag is enabled, false otherwise.
     */
    private async getOrderQuantityLimitsFeatureFlag(): Promise<boolean> {
        const defaultOrderQuantityLimitsFeatureConfig = this.props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits;
        if (defaultOrderQuantityLimitsFeatureConfig === 'none') {
            return false;
        }

        try {
            const featureStatuses = await this.props.data.featureState;
            const isQuantityLimitsFeatureEnabledInHq =
                featureStatuses.find(
                    featureState => featureState.Name === 'Dynamics.AX.Application.RetailDefaultOrderQuantityLimitsFeature'
                )?.IsEnabled || false;
            if (!isQuantityLimitsFeatureEnabledInHq) {
                return false;
            }
        } catch (error) {
            this.props.telemetry.warning(error);
            this.props.telemetry.debug('Unable to get feature states');
            return false;
        }

        if (defaultOrderQuantityLimitsFeatureConfig === 'all') {
            return true;
        }
        try {
            const customerInfo = await this.props.data.customerInformation;
            return (
                !!customerInfo &&
                ((defaultOrderQuantityLimitsFeatureConfig === 'b2b' && customerInfo.IsB2b) ||
                    (defaultOrderQuantityLimitsFeatureConfig === 'b2c' && !customerInfo.IsB2b))
            );
        } catch (error) {
            this.props.telemetry.warning(error);
            this.props.telemetry.debug('Unable to get customer info');
            return false;
        }
    }

    private async _validateCart(retry: boolean = true): Promise<void> {
        if (!this.isQuantityLimitsFeatureEnabled) {
            return AsyncResult.resolve();
        }

        const {
            data: { cart: cart }
        } = this.props;
        let cartResult: ICartState;
        try {
            cartResult = await cart;
        } catch (error) {
            this.props.telemetry.warning(error);
            this.props.telemetry.debug('Unable to get cart');
            return AsyncResult.resolve();
        }
        const cartVersion = cartResult.cart?.Version;
        if (cartVersion) {
            try {
                const validationResult: CartLineValidationResults = await validateForCheckoutAsync({ callerContext: this.props.context.actionContext }, cartResult?.cart?.Id, cartVersion);
                this.setState({ cartlinesErrors: validationResult.ValidationFailuresByCartLines || [] });
                return AsyncResult.resolve();
            } catch (error) {
                if (retry && error.name === 'Microsoft_Dynamics_Commerce_Runtime_InvalidCartVersion') {
                    try {
                        await cartResult.refreshCart({});
                    } catch (refreshError) {
                        this.props.telemetry.warning(refreshError);
                        this.props.telemetry.debug('Unable to refresh Cart');
                        return AsyncResult.resolve();
                    }
                    try {
                        await this._validateCart(false);
                        return AsyncResult.resolve();
                    } catch (validateError) {
                        this.props.telemetry.warning(validateError);
                        this.props.telemetry.debug('Unable to validate Cart');
                        return AsyncResult.resolve();
                    }
                }

                this.props.telemetry.warning(error);
                this.props.telemetry.debug('Unable to validate Cart');
                return AsyncResult.resolve();
            }
        } else {
            this.props.telemetry.warning('Cart version not found for Cart validation request.');
            return AsyncResult.resolve();
        }
    }

    private _generateAddToOrderTemplateDialogResources(resources: ICartResources): IAddToOrderTemplateDialogResources {
        return {
            addToOrderTemplateHeader: resources.addToOrderTemplateHeader,

            noOrderTemplatesMessage: resources.noOrderTemplatesMessage,
            noOrderTemplatesDescription: resources.noOrderTemplatesDescription,

            createAnOrderTemplateButtonText: resources.createAnOrderTemplateButtonText,
            createNewOrderTemplateButtonText: resources.createNewOrderTemplateButtonText,
            cancelOrderTemplateCreationButtonText: resources.cancelOrderTemplateCreationButtonText,

            selectTemplatesText: resources.selectTemplatesText,
            addToTemplateButtonText: resources.addToTemplateButtonText,
            lineItemsText: resources.lineItemsText
        };
    }

    private _generateItemAddedToOrderTemplateDialogResources(resources: ICartResources): IItemAddedToOrderTemplateDialogResources {
        return {
            viewOrderTemplateButtonText: resources.viewOrderTemplateButtonText,
            continueShoppingButtonText: resources.continueShoppingButtonText,
            itemAddedToOrderTemplateHeaderItemOneText: resources.itemAddedToOrderTemplateHeaderItemOneText,
            itemAddedToOrderTemplateHeaderItemFormatText: resources.itemAddedToOrderTemplateHeaderItemFormatText,
            itemAddedToOrderTemplateHeaderMessageText: resources.itemAddedToOrderTemplateHeaderMessageText,
            freePriceText: resources.freePriceText,
            originalPriceText: resources.originalPriceText,
            currentPriceText: resources.currentPriceText
        };
    }

    private _generateDuplicateItemsWhenAddingToOrderTemplateDialogResources(resources: ICartResources): IDuplicateItemsWhenAddingToOrderTemplateDialogResources {
        return {
            duplicatedProductsHeader: resources.duplicatedProductsHeader,
            duplicatedProductsDescription: resources.duplicatedProductsDescription,
            updateQuantityButtonText: resources.updateQuantityButtonText,
            cancelDuplicateItemsButtonText: resources.cancelDuplicateItemsButtonText
        };
    }

    private _generateOrderTemplateNameDialogResources(resources: ICartResources): IOrderTemplateNameDialogResources {
        return {
            orderTemplateHeaderLabel: resources.createOrderTemplateHeader,
            orderTemplateTitleLabel: resources.orderTemplateTitle,
            orderTemplateNameAriaLabel: resources.orderTemplateNameAriaLabel,
            orderTemplateDescription: resources.createOrderTemplateDescription,

            defaultOrderTemplateName: resources.defaultOrderTemplateName,

            orderTemplateButtonLabel: resources.createOrderTemplateButtonText,
            orderTemplateCancelButtonLabel: resources.cancelNewOrderTemplateCreationButtonText
        };
    }
}
export default CartView;

function _renderBackToShoppingButton(resources: ICartResources): JSX.Element | null {
    return (
        <Button
            className={'msc-cart__btn-backtoshopping'}
            onClick={_goToPreviousPage}
        >
            {resources.backToShoppingButtonTitle}
        </Button>
    );
}

function _goToPreviousPage(): void {
    window.history.back();
}

const _renderBOPISBlock = (cartLine: ICartlinesViewProps, resources: ICartResources, storeSelector: React.ReactNode | undefined): JSX.Element | null => {
    if (!cartLine.pickUpInStore || !storeSelector) {
        return null;
    }

    const { shipInsteadDisplayText, shipToAddressDisplayText, pickItUpDisplayText, pickUpAtStoreWithLocationText, changeStoreDisplayText } = resources;

    const isBopisSelected = cartLine.pickUpInStore.isBopisSelected;
    const toggleBopis = (event: React.MouseEvent<HTMLElement>) => { return cartLine.pickUpInStore && cartLine.pickUpInStore.callbacks.toggleBopis(!isBopisSelected); };
    const changeStore = (event: React.MouseEvent<HTMLElement>) => { return cartLine.pickUpInStore && cartLine.pickUpInStore.callbacks.toggleBopis(true); };

    return (
        <Node {...cartLine.pickUpInStore.ContainerProps}>
            <div className='msc-cart-line__bopis-method'>
                {isBopisSelected ? <span className='pick-up'>{pickUpAtStoreWithLocationText}</span> : <span className='ship'>{shipToAddressDisplayText}</span>}
            </div>
            {
                isBopisSelected &&
                <div className='msc-cart-line__bopis-fullfilment'>
                    <span className='msc-cart-line__bopis-fullfilment-store'>{cartLine.pickUpInStore.orgUnitName}</span>
                    <Button className='msc-cart-line__bopis-changestore' onClick={changeStore}>{changeStoreDisplayText}</Button>
                </div>
            }
            <Button className='msc-cart-line__bopis-btn' onClick={toggleBopis}>{isBopisSelected ? shipInsteadDisplayText : pickItUpDisplayText}</Button>
        </Node>
    );
};

const _renderErrorBlock = (errorData: IOrderSummaryErrors | undefined): JSX.Element | null => {
    if (!errorData || errorData.errors.length === 0) {
        return null;
    }
    return (
        <Node {...errorData.Wrapper}>
            {"Please review cart errors before proceeding to checkout"}
        </Node>
    );
};

const _renderUnder200sError = (props:any): JSX.Element | null => {
    const OrderSubTotal =  props.data.cart.result?.cart?.SubtotalAmount;
    const { request } = props.context;
    const market = request && request.apiSettings && request.apiSettings.oun;
    const isB2BI = market && market === "B2BI";
    let minimumOrder = 200;
    const discountLevel = props.data.customerInformation.result!.LineDiscountGroup;
    if(isB2BI) {
        switch(discountLevel){
            case "Level 1":
                minimumOrder = 1500;
                break;
            case "Level 4":
                minimumOrder = 500;
                break;
            case "Level 5":
                minimumOrder = 500;
                break;
            default:
                minimumOrder = 200;
        }
    }
    let isGiftCardOnly = props.data.cart.result?.cart && props.data.cart.result?.cart.CartLines && props.data.cart.result?.cart.CartLines.length === 1 && props.data.cart.result?.cart.CartLines[0].ItemId === "GiftCard";
    if(!isGiftCardOnly && OrderSubTotal < minimumOrder) {
        return (
            <div className='msc-alert msc-alert msc-alert-danger'>
                 <div>Your order must be a minimum of ${minimumOrder}. Please update your cart to place an order.</div>
            </div>
        );
    }
    return null
};

const _renderOrderSummarylines = (orderSummaryLines: IOrderSummaryLines | undefined, OrderSummaryItems: INodeProps, props: ICartViewProps & ICartProps<{}>): JSX.Element | null => {
    if (!orderSummaryLines) {
        return null;
    }
    const isB2B = props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits === 'b2b';
    return (
        <Node {...OrderSummaryItems}>
            {isB2B? null : props.promoCode}
            {isB2B? orderSummaryLines.subtotal : _renderSubTotal(props)}
            {props.config.hideTaxLine ? null : _renderTax(props)}
            {isB2B ? orderSummaryLines.subtotal : (orderSummaryLines.totalDiscounts ? orderSummaryLines.subtotal : null)}
            {isB2B? null : _renderAmountDue(props)}
            {isB2B? null : _renderTotalSavings(props)}
        </Node>
    );
};

function _renderSubTotal(props: ICartViewProps & ICartProps<{}>): JSX.Element | null {
    const OrderSubTotal =  props.data.cart.result?.cart?.NetPrice;
    return (
        <p className={`msc-order-summary__line-sub-total`}>
           {OrderSubTotal && OrderSubTotal > 0 ? (
            <>
                    <span className='msc-order-summary__label'>{props.resources.subTotalHeadingLabel}</span>
                    <PriceComponent
                        data={{
                            price: { CustomerContextualPrice: OrderSubTotal }
                        }}
                        freePriceText={undefined}
                        context={props.context}
                        id={props.id}
                        typeName={props.typeName}
                        className={'msc-order-summary__value'}
                    />
                </>
            ) : null}
        </p>
    );
}

function _renderTax(props: ICartViewProps & ICartProps<{}>): JSX.Element | null {
    const discountAmount = props.cart && props.cart.cart.DiscountAmount || 0;
    const taxLabel = discountAmount > 0? props.resources.estimatedShippingLabel : props.resources.estimatedTaxLabel;
    return (
        <p className={`msc-order-summary__line-amount-tax`}>
            <span className='msc-order-summary__label'>{taxLabel}</span>
            {props.data.cart.result?.cart?.TaxAmount && props.data.cart.result?.cart?.TaxAmount > 0 ? (
                <PriceComponent
                    data={{
                        price: { CustomerContextualPrice: props.data.cart.result?.cart?.TaxAmount }
                    }}
                    freePriceText={props.resources.priceFree}
                    context={props.context}
                    id={props.id}
                    typeName={props.typeName}
                    className={'msc-order-summary__value'}
                />
            ) : (
                    <span className='msc-order-summary__value'>{props.resources.yetToBeCalculatedText}</span>
                )}
        </p>
    );
}

function _renderAmountDue(props: ICartViewProps & ICartProps<{}>): JSX.Element | null {
    return (
        <p className={`msc-order-summary__line-amount-due`}>
            <span className='msc-order-summary__label'>{props.resources.amountDueLabel}</span>
            {props.data.cart.result?.cart?.TotalAmount || props.data.cart.result?.cart?.TotalAmount === 0 ? (
                <PriceComponent
                    data={{
                        price: { CustomerContextualPrice: props.data.cart.result?.cart?.TotalAmount }
                    }}
                    freePriceText={props.resources.priceFree}
                    context={props.context}
                    id={props.id}
                    typeName={props.typeName}
                    className={'msc-order-summary__value'}
                />
            ) : (
                    <span className='msc-order-summary__value'>{props.resources.yetToBeCalculatedText}</span>
                )}
        </p>
    );
}

function _renderTotalSavings(props: ICartViewProps): JSX.Element | null {

    const DiscountAmountWithoutTax = props.data.cart.result?.cart?.DiscountAmountWithoutTax;
    return (
        <p className={`msc-order-summary__line-total-savings`}>
            {DiscountAmountWithoutTax && DiscountAmountWithoutTax > 0 ? (
                <>
                    <span className='msc-order-summary__label'>{props.resources.totalSavings}</span>
                    <PriceComponent
                        data={{
                            price: { CustomerContextualPrice: DiscountAmountWithoutTax }
                        }}
                        freePriceText={props.resources.priceFree}
                        context={props.context}
                        id={props.id}
                        typeName={props.typeName}
                        className={'msc-order-summary__value'}
                    />
                </>
            ) : null}
        </p>
    );
}